import { useEffect, useState } from 'react'
import { FormControl, FormHelperText, FormLabel, Grid, Select } from '@chakra-ui/react'
import { geographicService, type GeographicOption } from '@/lib/services/geographicService'

export type PortalLocationState = {
  regionId: string
  provinceId: string
  townId: string
}

type Props = {
  value: PortalLocationState
  onChange: (patch: Partial<PortalLocationState>) => void
}

export function PortalLocationSection({ value, onChange }: Props) {
  const [regions, setRegions] = useState<GeographicOption[]>([])
  const [provinces, setProvinces] = useState<GeographicOption[]>([])
  const [towns, setTowns] = useState<GeographicOption[]>([])

  useEffect(() => {
    geographicService.getRegions().then(setRegions).catch(() => setRegions([]))
  }, [])

  useEffect(() => {
    if (!value.regionId) {
      setProvinces([])
      return
    }
    geographicService
      .getProvinces(Number(value.regionId))
      .then(setProvinces)
      .catch(() => setProvinces([]))
  }, [value.regionId])

  useEffect(() => {
    if (!value.provinceId) {
      setTowns([])
      return
    }
    geographicService
      .getTowns(Number(value.provinceId))
      .then(setTowns)
      .catch(() => setTowns([]))
  }, [value.provinceId])

  return (
    <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
      <FormControl isRequired>
        <FormLabel fontSize="sm">Region</FormLabel>
        <Select
          placeholder="Select region"
          value={value.regionId}
          onChange={(e) =>
            onChange({ regionId: e.target.value, provinceId: '', townId: '' })
          }
        >
          {regions.map((r) => (
            <option key={r.id} value={String(r.id)}>
              {r.label || r.name}
            </option>
          ))}
        </Select>
      </FormControl>

      <FormControl isRequired>
        <FormLabel fontSize="sm">Province</FormLabel>
        <Select
          placeholder="Select province"
          value={value.provinceId}
          isDisabled={!value.regionId}
          onChange={(e) => onChange({ provinceId: e.target.value, townId: '' })}
        >
          {provinces.map((p) => (
            <option key={p.id} value={String(p.id)}>
              {p.label || p.name}
            </option>
          ))}
        </Select>
      </FormControl>

      <FormControl isRequired>
        <FormLabel fontSize="sm">Town / City</FormLabel>
        <Select
          placeholder="Select town"
          value={value.townId}
          isDisabled={!value.provinceId}
          onChange={(e) => onChange({ townId: e.target.value })}
        >
          {towns.map((t) => (
            <option key={t.id} value={String(t.id)}>
              {t.label || t.name}
            </option>
          ))}
        </Select>
        <FormHelperText>We need your location to dispatch the right engineer.</FormHelperText>
      </FormControl>
    </Grid>
  )
}

export const DEFAULT_PORTAL_LOCATION: PortalLocationState = {
  regionId: '',
  provinceId: '',
  townId: '',
}
